import { Drawer, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import {
  type CreateWorkflowBody,
  type PlanType,
  StepType,
  type WorkflowStepsAttributes,
} from 'api/models';
import classNames from 'classnames/bind';
import { useCompanyConfiguration } from 'init/use-company-configuration';
import { i18nKeys, useTranslation } from 'locales';
import { Fragment, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { isNonNullish } from 'remeda';

import type { WorkflowStepsTarget } from '../../Workflows.constants';
import { openWorkflowTaskStepModal } from '../Step/openWorkflowTaskStepModal';
import { WorkflowFormStep } from '../Step/WorkflowFormStep';
import {
  getAvailableTemplates,
  getWorkflowRights,
  getWorkflowStepRights,
  isAfterDueDate,
  isBeforeDueDate,
} from '../WorkflowForm.utils';
import styleIdentifiers from './WorkflowFormSteps.module.scss';
import { WorkflowFormStepsButton } from './WorkflowFormStepsButton';
import { WorkflowFormStepsItem } from './WorkflowFormStepsItem';
import { WorkflowFormStepsLine } from './WorkflowFormStepsLine';

const styles = classNames.bind(styleIdentifiers);

type WorkflowFormStepsProps = {
  planType: PlanType;
  name: WorkflowStepsTarget;
};

export const WorkflowFormSteps = ({ planType, name }: WorkflowFormStepsProps) => {
  const { t } = useTranslation();

  const company = useCompanyConfiguration();

  const { canAddStep, canEditStep } = getWorkflowRights(company, planType);
  const { canAddTask, canEditTask } = getWorkflowStepRights(company, planType);

  const { watch, setValue } = useFormContext<CreateWorkflowBody>();

  const { fields, append, remove, insert, prepend } = useFieldArray<CreateWorkflowBody>({
    name,
  });

  const [stepForm, setStepForm] = useState<StepForm | null>(null);
  const [isStepFormDirty, setIsStepFormDirty] = useState(false);

  const closeStepForm = () => {
    setStepForm(null);
    setIsStepFormDirty(false);
  };

  const askBeforeClosingStepForm = () => {
    if (!isStepFormDirty) {
      closeStepForm();
      return;
    }

    modals.openConfirmModal({
      title: t(i18nKeys.ATTENTION),
      centered: true,
      children: <Text size="sm">{t(i18nKeys.FORM.SIDE_MENU.LOST_INFO)}</Text>,
      labels: { confirm: t(i18nKeys.CONFIRM), cancel: t(i18nKeys.CANCEL) },
      onConfirm: closeStepForm,
    });
  };

  const edit = (index: number) => {
    if (!canEditStep) {
      return false;
    }

    const step = watch(`${name}.${index}`);

    if (step.step_type === StepType.task_generation && canEditTask) {
      return openWorkflowTaskStepModal({
        title: t(i18nKeys.EDIT_TASK),
        step,
        onSubmit: data => setValue(`${name}.${index}`, data),
      });
    }

    const availableTemplates = getAvailableTemplates(planType, name, watch(name), index);

    return setStepForm({
      templates: availableTemplates,
      step,
      onSubmit: data => {
        setValue(`${name}.${index}`, data);
        closeStepForm();
      },
    });
  };

  const add = (index: number, action: (data: WorkflowStepsAttributes) => void) => {
    if (!canAddStep) {
      return false;
    }

    const availableTemplates = getAvailableTemplates(planType, name, watch(name), index);

    if (!availableTemplates.length && canAddTask) {
      return openWorkflowTaskStepModal({
        title: t(i18nKeys.ADD_TASK),
        onSubmit: action,
      });
    }

    return setStepForm({
      templates: availableTemplates,
      onSubmit: data => {
        action(data);
        closeStepForm();
      },
    });
  };

  const getButtonLabel = (index: number) =>
    getAvailableTemplates(planType, name, watch(name), index).length
      ? t(i18nKeys.ADD_STEP)
      : t(i18nKeys.ADD_TASK);

  return (
    <section className={styles('workflows-form-steps')}>
      {fields.map((step, i) => (
        <Fragment key={step.id}>
          {i === 0 ? (
            <WorkflowFormStepsLine bottom top={isAfterDueDate(name)}>
              <WorkflowFormStepsButton
                title={getButtonLabel(0)}
                onClick={() => add(0, data => prepend(data))}
                disabled={!canAddStep}
                label={isBeforeDueDate(name)}
              />
            </WorkflowFormStepsLine>
          ) : (
            <WorkflowFormStepsLine top bottom>
              <WorkflowFormStepsButton
                title={getButtonLabel(i)}
                onClick={() => add(i, data => insert(i, data))}
                disabled={!canAddStep}
              />
            </WorkflowFormStepsLine>
          )}
          <WorkflowFormStepsItem
            planType={planType}
            name={name}
            index={i}
            edit={() => edit(i)}
            remove={() => remove(i)}
          />
          {i === fields.length - 1 && (
            <WorkflowFormStepsLine top bottom={isBeforeDueDate(name)}>
              <WorkflowFormStepsButton
                title={getButtonLabel(i + 1)}
                onClick={() => add(i + 1, data => append(data))}
                disabled={!canAddStep}
                label={isAfterDueDate(name)}
              />
            </WorkflowFormStepsLine>
          )}
        </Fragment>
      ))}
      {!fields.length && (
        <WorkflowFormStepsLine top={isAfterDueDate(name)} bottom={isBeforeDueDate(name)}>
          <WorkflowFormStepsButton
            title={getButtonLabel(0)}
            onClick={() => add(0, data => append(data))}
            disabled={!canAddStep}
            label
          />
        </WorkflowFormStepsLine>
      )}
      <Drawer position="right" opened={isNonNullish(stepForm)} onClose={askBeforeClosingStepForm}>
        {isNonNullish(stepForm) && (
          <WorkflowFormStep
            planType={planType}
            templates={stepForm.templates}
            step={stepForm.step}
            onSubmit={stepForm.onSubmit}
            onClose={closeStepForm}
            onDirtyChange={setIsStepFormDirty}
          />
        )}
      </Drawer>
    </section>
  );
};

type StepForm = {
  templates: StepType[];
  onSubmit: (data: WorkflowStepsAttributes) => void;
  step?: WorkflowStepsAttributes;
};
